import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faTimes,
  faTasks,
} from "@fortawesome/free-solid-svg-icons";

const SprintCentralAgileBoard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { projId } = useParams();

  const authState = useSelector((state) => state.authReducer || {});
  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "User";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projId || "");
  const [tasks, setTasks] = useState([]);

  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState(null);

  const initialForm = {
    title: "",
    assignee: "",
    priority: "Medium",
    status: "Backlog",
  };

  const [formData, setFormData] = useState(initialForm);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // ---------------- SAMPLE DATA ----------------

  const demoProjects = [
    { id: 1, name: "Website Redesign 2026" },
    { id: 2, name: "ERP Integration Phase 1" },
  ];

  const demoTasks = [
    {
      id: 1,
      projectId: 1,
      title: "Design Homepage",
      assignee: "Priya",
      priority: "High",
      status: "Backlog",
    },
    {
      id: 2,
      projectId: 1,
      title: "Build Login API",
      assignee: "Rahul",
      priority: "High",
      status: "To Do",
    },
    {
      id: 3,
      projectId: 1,
      title: "Integrate Payment Gateway",
      assignee: "Amit",
      priority: "Medium",
      status: "In Progress",
    },
    {
      id: 4,
      projectId: 1,
      title: "Test User Signup",
      assignee: "Neha",
      priority: "Medium",
      status: "Review",
    },
    {
      id: 5,
      projectId: 1,
      title: "Deploy to Production",
      assignee: "DevOps",
      priority: "Low",
      status: "Done",
    },
  ];

  const loadSample = () => {
    setProjects(demoProjects);
    setTasks(demoTasks);
  };

  // ---------------- CRUD ----------------

  const handleAdd = () => {
    if (!selectedProjectId) {
      alert("Please select a project first.");
      return;
    }

    setEditingTask(null);
    setFormData(initialForm);
    setShowModal(true);
  };

  const handleEdit = (task) => {
    setEditingTask(task);

    setFormData({
      title: task.title,
      assignee: task.assignee,
      priority: task.priority,
      status: task.status,
    });

    setShowModal(true);
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this task?")) return;

    setTasks((prev) => prev.filter((t) => t.id !== id));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title) {
      alert("Task title required.");
      return;
    }

    const newTask = {
      id: editingTask?.id || Date.now(),
      projectId: Number(selectedProjectId),
      ...formData,
    };

    if (editingTask) {
      setTasks((prev) =>
        prev.map((t) => (t.id === editingTask.id ? newTask : t)),
      );
    } else {
      setTasks((prev) => [...prev, newTask]);
    }

    setShowModal(false);
    setFormData(initialForm);
  };

  // ---------------- FILTER ----------------

  const projectTasks = tasks.filter(
    (t) => String(t.projectId) === String(selectedProjectId),
  );

  const filtered = projectTasks.filter(
    (t) =>
      t.title.toLowerCase().includes(search.toLowerCase()) ||
      t.assignee.toLowerCase().includes(search.toLowerCase()),
  );

  const columns = ["Backlog", "To Do", "In Progress", "Review", "Done"];

  const updateStatus = (task, newStatus) => {
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, status: newStatus } : t)),
    );
  };

  const priorityColor = {
    High: "bg-red-600",
    Medium: "bg-yellow-600",
    Low: "bg-green-600",
  };

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100 p-2">
      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-black border-b border-blue-500/40 px-4 py-3 flex items-center justify-between">
        <div className="flex flex-col">
          <div className="text-lg lg:text-xl font-bold text-cyan-300">
            📋 SprintCentral Agile Board
          </div>
          <div className="text-xs text-gray-400">Agile Kanban Workflow</div>
        </div>

        <div className="hidden md:flex items-center gap-4">
          {isLoggedIn ? (
            <>
              <span className="text-xs text-teal-300">{userName}</span>

              <button
                onClick={handleLogout}
                className="w-12 px-2 py-1 bg-red-700/80 hover:bg-red-600 rounded-md text-sm"
              >
                ⏻
              </button>
            </>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="w-12 px-2 py-1 border border-teal-600 rounded-md"
            >
              🔐
            </button>
          )}
        </div>
      </header>

      <div className="mt-4 min-h-[75vh]">
        {/* ACTION BAR */}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <button
            onClick={loadSample}
            className="text-xs lg:text-sm w-40 lg:w-64 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> Sample Tasks
          </button>

          <button
            onClick={handleAdd}
            className="text-xs lg:text-sm w-48 px-2 py-2 bg-cyan-600 hover:bg-cyan-500 rounded-lg"
          >
            <FontAwesomeIcon icon={faPlus} /> New Task
          </button>

          {!projId && (
            <select
              value={selectedProjectId}
              onChange={(e) => setSelectedProjectId(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg h-12 px-3 py-2"
            >
              <option value="">Select Project</option>

              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          <div className="relative w-full">
            <FontAwesomeIcon
              icon={faSearch}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
            />

            <input
              type="text"
              placeholder="Search tasks..."
              value={search}
              style={{ width: "100%" }}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg"
            />
          </div>
        </div>

        {/* KANBAN BOARD */}

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mt-6">
          {columns.map((col) => (
            <div key={col} className="bg-gray-800 rounded-xl p-3">
              <h3 className="text-sm font-semibold text-cyan-300 mb-3">
                {col}
              </h3>

              {filtered
                .filter((t) => t.status === col)
                .map((task) => (
                  <div
                    key={task.id}
                    className="bg-gray-700 rounded-lg p-3 mb-3"
                  >
                    <div className="flex justify-between">
                      <span className="text-sm font-semibold">
                        {task.title}
                      </span>

                      <span
                        className={`text-xs px-2 py-1 rounded ${priorityColor[task.priority]}`}
                      >
                        {task.priority}
                      </span>
                    </div>

                    <div className="text-xs text-gray-300 mt-1">
                      👤 {task.assignee}
                    </div>

                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => handleEdit(task)}
                        className="p-1 bg-gray-600 rounded"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </button>

                      <button
                        onClick={() => handleDelete(task.id)}
                        className="p-1 bg-gray-600 rounded"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>

                    <select
                      value={task.status}
                      onChange={(e) => updateStatus(task, e.target.value)}
                      className="mt-2 w-full bg-gray-900 text-xs border border-gray-600 rounded p-1"
                    >
                      {columns.map((c) => (
                        <option key={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                ))}
            </div>
          ))}
        </div>
      </div>

      {/* MODAL */}

      {showModal && (
        <div className="bg-black w-full rounded-lg p-4 border border-cyan-200 shadow-xl mt-2">
          <div className="flex justify-between mb-4">
            <h2 className="text-lg text-cyan-200">
              {editingTask ? "Edit Task" : "New Task"}
            </h2>

            <button
              onClick={() => setShowModal(false)}
              className="w-12 bg-blue-500"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <input
              type="text"
              placeholder="Task Title"
              required
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              className="w-full bg-gray-900 border border-gray-700 rounded p-2"
            />

            <input
              type="text"
              placeholder="Assignee"
              value={formData.assignee}
              onChange={(e) =>
                setFormData({ ...formData, assignee: e.target.value })
              }
              className="w-full bg-gray-900 border border-gray-700 rounded p-2"
            />

            <select
              value={formData.priority}
              onChange={(e) =>
                setFormData({ ...formData, priority: e.target.value })
              }
              className="w-40 bg-gray-900 border border-gray-700 rounded p-2"
            >
              <option>High</option>
              <option>Medium</option>
              <option>Low</option>
            </select>

            <button
              type="submit"
              className="ml-4 w-40 bg-cyan-600 hover:bg-cyan-500 rounded p-2"
            >
              {editingTask ? "Update Task" : "Create Task"}
            </button>
          </form>
        </div>
      )}

      {/* FOOTER */}

      <footer className="bg-black border-t border-blue-400 text-white py-4 mt-6">
        <div className="mx-auto px-3 flex flex-col lg:flex-row items-center justify-between gap-3">
          <div className="text-sm text-yellow-200">
            © 2026 Think5Whys Global DeepTech Solutions.
          </div>

          <div className="flex flex-wrap gap-4 text-sm">
            <Link to="/about-us" className="hover:text-teal-300">
              About
            </Link>
            <Link to="/privacy-policy" className="hover:text-teal-300">
              Privacy Policy
            </Link>
            <Link to="/terms-of-use" className="hover:text-teal-300">
              Terms of Use
            </Link>
            <Link to="/contact-us" className="hover:text-teal-300">
              Contact
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SprintCentralAgileBoard;
